import { HttpStatusCode } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import {
    CellCheckboxComponent,
    CellSelectComponent,
    TapestryNotificationService
} from '@predictiveindex/tapestry';
import { ColDef } from 'ag-grid-community';
import {
    ModuleUserAccessLevel,
    UserManagementBatchAction,
    UserAccessSoftwareModule
} from '../../../constants-types';
import {
    IconType,
    ModuleName,
    ModuleUserInviteRequest,
    ModuleUserUpdateRequest,
    ModuleUser
} from '../../../core-data-model';
import { CompaniesDataService, ModuleUserApi } from '../../../core-services';
import { GuidUtilities } from '../../../core-utilities';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService
} from '../../../system-services';
import { TranslationService } from '../../../translations';
import { ResendInviteCellRendererComponent } from '../../resend-invite-cell-renderer.component';
import { AccessLevelHeaderTooltipComponent } from '../access-level-header-tooltip/access-level-header-tooltip.component';
import { HeaderWithIconComponent } from '../header-with-icon/header-with-icon.component';
import { ModuleUserManagementService } from '../service/module-user-management.service';

@Component({
    selector: 'pi-module-user-management-grid',
    template: `
        <pi-user-grid
            [columnDefinition]="columnDefs"
            [rowData]="rowData"
            [showToggleCheckbox]="true"
            (toggleCheckBox)="toggleHideNoAccessUsers($event)"
            [frameworkComponents]="frameworkComponents"
        ></pi-user-grid>
    `,
    styleUrls: ['./module-user-management-grid.component.less'],
    standalone: false
})
export class ModuleUserManagementComponent implements OnInit {
    @Input() moduleName: ModuleName;

    rowData: ModuleUser[];
    checkboxDisabled = false;
    excludeNoAccess: boolean;
    accessLevel: PermissionsModuleAccessLevel;
    columnDefs: ColDef[];

    frameworkComponents = {
        cellCheckBox: CellCheckboxComponent,
        cellSelect: CellSelectComponent
    };
    moduleCheckboxTranslationKeyMap = {
        [ModuleName.Hire]: {
            header: this.TR.key('module-userManage.vmHeader.hire'),
            toolTip: this.TR.key('module-userManage.vmToolTip.hire')
        },
        [ModuleName.Design]: {
            header: this.TR.key('module-userManage.vmHeader.design'),
            toolTip: this.TR.key('module-userManage.vmToolTip.design')
        },
        [ModuleName.Inspire]: {
            header: this.TR.key('module-userManage.vmHeader.inspire'),
            toolTip: this.TR.key('module-userManage.vmToolTip.inspire')
        },
        // 8/30/22: VMs have not been decided for Diagnose yet. These keys do not yet exist in translations
        // and will need to be added before VMs can be activated.
        [ModuleName.Diagnose]: {
            header: this.TR.key('module-userManage.vmHeader.diagnose'),
            toolTip: this.TR.key('module-userManage.vmToolTip.diagnose')
        }
    };

    accessLevelOptions = [
        {
            value: ModuleUserAccessLevel.NoAccess,
            displayValue: this.TR.key('module-user.accessLevel.noaccess')
        },
        {
            value: ModuleUserAccessLevel.Limited,
            displayValue: this.TR.key('module-user.accessLevel.limited')
        },
        {
            value: ModuleUserAccessLevel.Full,
            displayValue: this.TR.key('module-user.accessLevel.full')
        },
        {
            value: ModuleUserAccessLevel.Admin,
            displayValue: this.TR.key('module-user.accessLevel.admin')
        }
    ];

    constructor(
        public readonly TR: TranslationService,
        public readonly initDataService: InitDataService,
        private readonly tapestryNotification: TapestryNotificationService,
        private readonly sharedService: ModuleUserManagementService,
        private readonly companyService: CompaniesDataService,
        private readonly moduleUserApi: ModuleUserApi,
        private readonly featureFlagService: FeatureFlagsService,
        private readonly guidUtility: GuidUtilities
    ) {
        sharedService.moduleGrid = this;
    }

    updateUserAccess(
        updatedAccessLevel: ModuleUserAccessLevel,
        userId: string
    ) {
        const moduleUserUpdate: ModuleUserUpdateRequest = {
            module: this.moduleName,
            userBatchRequests: [
                {
                    module: this.moduleName,
                    userId: userId,
                    accessLevel: updatedAccessLevel,
                    batchAction: UserManagementBatchAction.Update
                }
            ]
        };

        return this.moduleUserApi
            .updateUserAccess(moduleUserUpdate)
            .then(() => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'notification.userAccessUpdated-success'
                    ),
                    duration: 3000,
                    type: 'success'
                });
            })
            .catch((reason: string) => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'notification.userAccessUpdated-failure'
                    ),
                    duration: 3000,
                    type: 'error'
                });
                throw new Error(reason);
            })
            .finally(() => {
                // update may be unsuccessful, so we update the grid to ensure that the correct setting is shown even in failure
                void this.refreshGridData();
            });
    }

    updateUserVMAccess(enableVM: boolean, userId: string) {
        const moduleUserUpdate: ModuleUserUpdateRequest = {
            module: this.moduleName,
            userBatchRequests: [
                {
                    module: this.moduleName,
                    userId: userId,
                    batchAction: enableVM
                        ? UserManagementBatchAction.Create
                        : UserManagementBatchAction.Delete
                }
            ]
        };

        return this.moduleUserApi
            .updateVMAccess(moduleUserUpdate)
            .then(() => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'notification.userAccessUpdated-success'
                    ),
                    duration: 3000,
                    type: 'success'
                });
            })
            .catch((reason: string) => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'notification.userAccessUpdated-failure'
                    ),
                    duration: 3000,
                    type: 'error'
                });
                throw new Error(reason);
            })
            .finally(() => {
                // update may be unsuccessful, so we update the grid to ensure that the correct setting is shown even in failure
                this.refreshGridData();
            });
    }

    /**
     * WARNING! This function is passed into the cellRendererFramework which means that the cells may be constructed
     * before this component. Any class variables that are assumed to be available inside this component are not
     * guaranteed to be initialized by the time ag-grid constructs the data cell.
     * Note above in the columnDefs that class variables required for this function are provided as PARAMS to the cell
     */
    resendModuleInvitation(params: any): Promise<any> {
        const moduleUser = params.data as ModuleUser;
        const invitation: ModuleUserInviteRequest = {
            usersToInvite: [
                {
                    userId: null,
                    email: moduleUser.email
                }
            ],
            softwareModule:
                UserAccessSoftwareModule[
                    params.moduleName as keyof typeof UserAccessSoftwareModule
                ],
            moduleAccessLevel: moduleUser.accessLevel,
            companyName: params.currentCompanyName,
            senderFirstName: params.senderProfile.firstName,
            senderLastName: params.senderProfile.lastName
        };

        return params.moduleUserApi
            .inviteUserAccess(invitation)
            .then(([responseBody, httpResponse]) => {
                if (
                    httpResponse.status == HttpStatusCode.Ok &&
                    responseBody.errors.length == 0
                ) {
                    // we should probably refresh the grid with some other value for after the resend (no mockup yet)
                    return Promise.resolve;
                } else {
                    return Promise.reject(new Error(httpResponse.statusText));
                }
            })
            .then(() => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'userManagement.resendInvitation-notification'
                    ),
                    duration: 3000,
                    type: 'success'
                });
            })
            .catch((reason: string) => {
                this.tapestryNotification.create({
                    message: this.TR.key(
                        'userManagement.resendInvitation-notification-failure'
                    ),
                    duration: 3000,
                    type: 'error'
                });
                throw new Error(reason);
            });
    }

    async ngOnInit() {
        this.accessLevel =
            (await this.initDataService.userAccessLevelMap.get(this.moduleName)
                ?.userAccessLevel) || PermissionsModuleAccessLevel.NoAccess;
        await this.refreshGridData();

        this.columnDefs = [
            {
                headerName: this.TR.key('label.common-terms.name'),
                field: 'name',
                sortable: true,
                filter: true,
                resizable: true,
                sort: 'asc',
                valueGetter: (params) =>
                    params.data.firstName + ' ' + params.data.lastName,
                comparator: (_valueA, _valueB, nodeA, nodeB) => {
                    if (nodeA.data.lastName == nodeB.data.lastName) return 0;
                    return nodeA.data.lastName > nodeB.data.lastName ? 1 : -1;
                },
                cellRenderer: ResendInviteCellRendererComponent,
                cellRendererParams: {
                    moduleName: this.moduleName,
                    moduleUserApi: this.moduleUserApi,
                    snackbarService: this.tapestryNotification,
                    TR: this.TR,
                    senderProfile: this.initDataService.profile,
                    currentCompanyName: this.companyService.currentCompanyName,
                    enableLink:
                        this.initDataService.isSuperAdmin() ||
                        this.accessLevel ===
                            PermissionsModuleAccessLevel.AllModuleAdmin ||
                        this.accessLevel ===
                            PermissionsModuleAccessLevel.AllCompanyAdmin,
                    action: this.resendModuleInvitation
                }
            },
            {
                headerName: this.TR.key('label.common-terms.email'),
                field: 'email',
                sortable: true,
                filter: true,
                resizable: true
            },
            {
                headerComponent: HeaderWithIconComponent,
                headerComponentParams: {
                    displayName: this.TR.key('userManagement.accessLevel'),
                    iconType: IconType.Info2
                },
                headerTooltip: 'accessLevel',
                tooltipComponent: AccessLevelHeaderTooltipComponent,
                field: 'accessLevel',
                sortable: true,
                resizable: true,
                cellClass: 'gridDropdownCell',
                cellRenderer: 'cellSelect',
                cellRendererParams: (params: any) => ({
                    disabled: this.disableEdit(params.data),
                    options: this.accessLevelOptions,
                    selectionChanged: (value, data) => {
                        this.updateUserAccess(value, data.userId);
                    }
                })
            },
            {
                hide: this.hideCanUseVMs(this.moduleName),
                headerName:
                    this.moduleCheckboxTranslationKeyMap[this.moduleName]
                        .header,
                field: 'canUseVms',
                sortable: true,
                cellRenderer: 'cellCheckBox',
                cellStyle: { 'text-align': 'center' },
                headerClass: 'ag-centered-header',

                cellRendererParams: (params: any) => ({
                    toolTip:
                        this.moduleCheckboxTranslationKeyMap[this.moduleName]
                            .toolTip,
                    disabled:
                        this.disableEdit(params.data) ||
                        this.disableVMEdit(params.data),
                    checkChanged: (checkBox: MatCheckboxChange, data) => {
                        this.updateUserVMAccess(checkBox.checked, data.userId);
                    }
                })
            }
        ];
    }

    disableEdit(data: ModuleUser) {
        // Super Admin can Edit any Users, including Admin
        if (this.initDataService.isSuperAdmin()) {
            return false;
        }

        if (data.isCompanyAdmin) {
            return true;
        }
        if (!data.userId || data.userId == this.guidUtility.getEmpty()) {
            return true;
        }

        return this.accessLevel ===
            PermissionsModuleAccessLevel.AllModuleAdmin ||
            this.accessLevel === PermissionsModuleAccessLevel.AllCompanyAdmin
            ? data.userId === this.initDataService.user.userGuid
            : true;
    }

    disableVMEdit(data: ModuleUser) {
        return !this.initDataService.isSuperAdmin()
            ? data.accessLevel === ModuleUserAccessLevel.Admin ||
                  data.accessLevel === ModuleUserAccessLevel.CompanyAdmin ||
                  data.accessLevel === ModuleUserAccessLevel.NoAccess
            : false;
    }

    toggleHideNoAccessUsers(hideNoAccessUser: boolean) {
        this.excludeNoAccess = hideNoAccessUser;
        void this.refreshGridData();
    }

    async refreshGridData() {
        this.rowData = await this.moduleUserApi
            .getModuleUsers(this.moduleName, this.excludeNoAccess)
            .catch((reason: string) => {
                this.tapestryNotification.create({
                    message: this.TR.key('text.error.header3'),
                    duration: 3000,
                    type: 'error'
                });
                throw new Error(reason);
            });
        this.sharedService.initData();
    }

    private hideCanUseVMs(moduleName: ModuleName) {
        switch (moduleName) {
            case ModuleName.Design:
                return !this.featureFlagService.getByFlag(
                    FeatureFlags.enableVMUsageUserMgmtColumnDesign
                );
            case ModuleName.Hire:
                return !this.featureFlagService.getByFlag(
                    FeatureFlags.enableVMUsageUserMgmtColumnHire
                );
            case ModuleName.Diagnose:
                return !this.featureFlagService.getByFlag(
                    FeatureFlags.enableVMUsageUserMgmtColumnDiagnose
                );
            default:
                return true;
        }
    }
}
